import React, { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { BaseWidgetProps } from "../BaseProps";
import { useSetWidgetFocus } from "../useSetFocus";
import { FlatList, ListRenderItem, Platform } from "react-native";
import {
  AHSeparator,
  AHTeamIconCircle,
  AHText,
  AHWidgetTitle,
  TouchableBox,
} from "arkh";
import { WidgetTodoSummary_widgetTodoSummary$key } from "./__generated__/WidgetTodoSummary_widgetTodoSummary.graphql";

export function WidgetTodoSummary(
  props: { widget: WidgetTodoSummary_widgetTodoSummary$key } & BaseWidgetProps
) {
  const widget = useFragment(
    graphql`
      fragment WidgetTodoSummary_widgetTodoSummary on WidgetTodoSummary {
        id
        title
        category @required(action: THROW) {
          id
          title
        }
        teams {
          id
          notResolvedCount
          team @required(action: THROW) {
            id
            name
            color
            icon
          }
        }
      }
    `,
    props.widget
  );

  const wrapperStyle = useMemo(() => ({ flex: 1 }), []);

  const teamIds = useMemo(
    () => widget?.teams?.map((t) => t.id) ?? [],
    [widget.teams]
  );
  useSubscriptionWithSkip(
    useMemo(
      () => ({
        subscription: graphql`
          subscription WidgetTodoSummaryTodoCreatedSubscription(
            $teamIds: [ID!]!
          ) {
            todoCreated(teamIds: $teamIds) {
              category {
                id
                teams(ids: $teamIds) {
                  id
                  notResolvedCount
                }
              }
            }
          }
        `,
        variables: { teamIds },
      }),
      [teamIds]
    ),
    { skip: props.interactive === false }
  );
  useSubscriptionWithSkip(
    useMemo(
      () => ({
        subscription: graphql`
          subscription WidgetTodoSummaryTodoChangedSubscription(
            $teamIds: [ID!]!
          ) {
            todoChanged(teamIds: $teamIds) {
              category {
                id
                teams(ids: $teamIds) {
                  id
                  notResolvedCount
                }
              }
            }
          }
        `,
        variables: { teamIds },
      }),
      [teamIds]
    ),
    { skip: props.interactive === false }
  );
  useSubscriptionWithSkip(
    useMemo(
      () => ({
        subscription: graphql`
          subscription WidgetTodoSummaryTodoDeletedSubscription(
            $teamIds: [ID!]!
            $categoryIds: [ID!]!
          ) {
            todoDeleted(categoryIds: $categoryIds, teamIds: $teamIds) {
              category {
                id
                teams(ids: $teamIds) {
                  id
                  notResolvedCount
                }
              }
            }
          }
        `,
        variables: { teamIds, categoryIds: [widget.category.id] },
      }),
      [teamIds, widget.category.id]
    ),
    { skip: props.interactive === false }
  );
  const { onEvent } = props;

  const focus = useSetWidgetFocus(widget.id, props.storecastId);
  const onPressItem = useCallback(
    (teamId: string, teamName: string) => {
      if (props.mode === "FOCUS") {
        // focus(id);
      } else {
        onEvent?.({
          kind: "TodoCategory",
          categoryId: widget.category.id,
          categoryTitle: widget.category.title,
          teamName,
          teamId,
        });
      }
    },
    [onEvent, props.mode, widget.category.id, widget.category.title]
  );

  const onBackgroundClick = useCallback(() => {
    if (props.mode === "FOCUS") {
      focus();
    }
  }, [focus, props.mode]);

  const renderItem: ListRenderItem<(typeof widget.teams)[number]> = useCallback(
    ({ item }) => {
      return (
        <TouchableBox
          style={{ flexDirection: "row", alignItems: "center" }}
          spaceHorizontal="small"
          spaceVertical="small"
          onPress={() => {
            onPressItem(item.team.id, item.team.name);
          }}
        >
          {item.team ? (
            <AHTeamIconCircle
              color={item.team.color}
              type={item.team.icon}
              size="smallMedium"
            />
          ) : null}
          <AHText
            spaceLeft="small"
            type="title-small"
            style={{
              flex: 1,
            }}
          >
            {item.team?.name}
          </AHText>
          <AHText type="title-small">{item.notResolvedCount}</AHText>
        </TouchableBox>
      );
    },
    [onPressItem]
  );

  const teams = useMemo(
    () =>
      widget.teams
        ?.slice()
        .sort((a, b) => b.notResolvedCount - a.notResolvedCount) ?? [],
    [widget.teams]
  );

  return (
    <TouchableBox
      disabled={Platform.OS !== "web" && props.mode !== "FOCUS"}
      onPress={onBackgroundClick}
      style={wrapperStyle}
      color="surface"
    >
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        data={teams}
        renderItem={renderItem}
        scrollEnabled={props.interactive !== false}
        showsVerticalScrollIndicator={Platform.OS !== "web"}
      />
    </TouchableBox>
  );
}
